import { useTranslation } from 'react-i18next';
import { Callout } from '@/components/ui';
import type { ConnectionCheckProbe, ConnectionCheckReport } from '@/api/console';
import styles from './AppDetailConnectionTab.module.css';

const PROBE_TONE: Record<string, string> = {
    ok: styles.isOk,
    degraded: styles.isDegraded,
    failed: styles.isFailed,
    skipped: styles.isSkipped,
};

const PROBE_LABEL_KEYS: Record<string, string> = {
    kc_client_exists: 'console.appDetail.connection.check.probeClient',
    // EAA-1709: the machine client `<code>-api` (exists, enabled, service account, push role).
    kc_api_client: 'console.appDetail.connection.check.probeApiClient',
    redirect_uris: 'console.appDetail.connection.check.probeRedirectUris',
    audience_mapper: 'console.appDetail.connection.check.probeAudience',
    app_roles_catalog: 'console.appDetail.connection.check.probeRolesCatalog',
    backchannel_logout: 'console.appDetail.connection.check.probeBackchannelLogout',
    app_me_access: 'console.appDetail.connection.check.probeMeAccess',
};

const OVERALL_TONE: Record<string, 'info' | 'warning' | 'danger'> = {
    ok: 'info',
    degraded: 'warning',
};

function ProbeRow({ probe }: { probe: ConnectionCheckProbe }) {
    const { t } = useTranslation();
    const label = PROBE_LABEL_KEYS[probe.id] ? t(PROBE_LABEL_KEYS[probe.id]) : probe.id;
    return (
        <li className={styles.probeRow} data-testid="connection-check-probe">
            <span className={`${styles.probeStatus} ${PROBE_TONE[probe.status] ?? ''}`}>
                {t(`console.appDetail.connection.check.status.${probe.status}`)}
            </span>
            <div className={styles.probeBody}>
                <strong className={styles.probeTitle}>{label}</strong>
                <span className={styles.probeText}>{probe.detail}</span>
                {probe.remedy && (
                    <span className={`${styles.probeText} ${styles.probeRemedy}`}>
                        {probe.remedy}
                    </span>
                )}
            </div>
        </li>
    );
}

/**
 * EAA-1709: the result of one `POST …/connection-check` — the overall verdict plus
 * every probe's status, detail and remedy. Extracted from the "Kết nối kỹ thuật"
 * tab so the approval check on the page header and in the registry's enable
 * modal render the same rows the tab does.
 */
export function ConnectionCheckResult({ report }: { report: ConnectionCheckReport }) {
    const { t } = useTranslation();
    return (
        <>
            <Callout tone={OVERALL_TONE[report.overall] ?? 'danger'}>
                {t(`console.appDetail.connection.check.overall.${report.overall}`, {
                    time: new Date(report.checked_at).toLocaleTimeString('vi-VN'),
                })}
            </Callout>
            <ul className={styles.probeList}>
                {report.probes.map((probe) => (
                    <ProbeRow key={probe.id} probe={probe} />
                ))}
            </ul>
        </>
    );
}

export default ConnectionCheckResult;
